<script lang="ts">
  import { api, ApiError } from '$shared/api';
  import PasswordInput from '$shared/PasswordInput.svelte';
  import {
    BIRTH_MONTHS,
    NEUTRAL_REFUSAL,
    birthYears,
    composeBirthMonth,
    isAgeRefusal,
    signupHoldActive,
    startSignupHold,
  } from '$lib/ageScreen';
  import { login, startGoogleLogin, startTikTokLogin, startSpotifyLogin, startAppleLogin } from '$lib/auth.svelte';
  import { navigate } from '$lib/router.svelte';
  import { trackEvent } from '$lib/analytics.svelte';

  // Three-phase registration:
  //   'gate'    — no invite code yet: enter code or join waitlist
  //   'options' — valid code in hand: pick Google / TikTok / Email
  //   'form'    — full email/password form (reached from options or from gate→form for back-compat)
  let phase = $state<'gate' | 'options' | 'form'>('gate');
  let inviterUsername = $state('');

  // Gate phase
  let inviteCode = $state('');
  let gateError = $state('');
  let gateLoading = $state(false);

  // Signup form phase
  let username = $state('');
  let email = $state('');
  let password = $state('');
  let birthYear = $state('');
  let birthMonth = $state('');
  let holdActive = $state(signupHoldActive());
  let privacyAccepted = $state(false);
  let regError = $state(signupHoldActive() ? NEUTRAL_REFUSAL : '');
  let regLoading = $state(false);

  // Username live-validation. Status drives the hint color via field-hint--{status}.
  //   '' = empty/unchecked, 'format' = bad chars/length, 'checking' = API in flight,
  //   'taken' = name in use, 'available' = ok, 'error' = check failed (server still validates).
  let usernameStatus = $state<'' | 'format' | 'checking' | 'taken' | 'available' | 'error'>('');
  let usernameHint = $state('');
  // Race-safety: each $effect run bumps this; stale responses are dropped.
  let usernameCheckSeq = 0;
  let usernameDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  const USERNAME_RE = /^[a-zA-Z0-9_]{2,30}$/;

  const years = birthYears();

  // Waitlist
  let waitlistEmail = $state('');
  let waitlistMsg = $state('');
  let waitlistLoading = $state(false);

  // OAuth signup — carries the invite_code through the redirect.
  let googleLoading = $state(false);
  let tiktokLoading = $state(false);
  let spotifyLoading = $state(false);
  let appleLoading = $state(false);
  let optionsError = $state('');

  async function handleGoogleSignup() {
    if (holdActive) return;
    googleLoading = true;
    optionsError = '';
    try {
      const url = await startGoogleLogin(inviteCode.trim());
      window.location.href = url;
    } catch (err) {
      optionsError = (err as Error).message || 'Could not connect to Google';
      googleLoading = false;
    }
  }

  async function handleTikTokSignup() {
    if (holdActive) return;
    tiktokLoading = true;
    optionsError = '';
    try {
      const url = await startTikTokLogin(inviteCode.trim());
      window.location.href = url;
    } catch (err) {
      optionsError = (err as Error).message || 'Could not connect to TikTok';
      tiktokLoading = false;
    }
  }

  async function handleSpotifySignup() {
    if (holdActive) return;
    spotifyLoading = true;
    optionsError = '';
    try {
      const url = await startSpotifyLogin(inviteCode.trim());
      window.location.href = url;
    } catch (err) {
      optionsError = (err as Error).message || 'Could not connect to Spotify';
      spotifyLoading = false;
    }
  }

  async function handleAppleSignup() {
    if (holdActive) return;
    appleLoading = true;
    optionsError = '';
    try {
      const url = await startAppleLogin(inviteCode.trim());
      window.location.href = url;
    } catch (err) {
      optionsError = (err as Error).message || 'Could not connect to Apple';
      appleLoading = false;
    }
  }

  // Deep link: #/register?code=abc123 → validate then show 3-option chooser.
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const codeParam = hashParams.get('code');
  if (codeParam) {
    inviteCode = codeParam;
    // Phase stays at 'gate' visually until validate-invite returns; on success
    // we jump straight to 'options' so the user never sees the gate.
    void validateAndEnter(codeParam);
  }

  // OAuth callback may have bounced us back here with an invite_required /
  // invite_invalid error — surface it in the gate.
  const oauthErr = sessionStorage.getItem('register_invite_error');
  if (oauthErr) {
    sessionStorage.removeItem('register_invite_error');
    // Strip the "invite_required:" / "invite_invalid:" prefix for display.
    gateError = oauthErr.replace(/^invite_(required|invalid):\s*/, '');
  }

  // Auto-focus first input when phase changes
  let codeInput = $state<HTMLInputElement>();
  let usernameInput = $state<HTMLInputElement>();
  $effect(() => {
    if (phase === 'gate') codeInput?.focus();
    else if (phase === 'form') usernameInput?.focus();
  });

  // Live username validation: synchronous format check, then debounced availability.
  // Server is still source of truth at signup time; this is advisory UX only.
  $effect(() => {
    const value = username.trim();
    clearTimeout(usernameDebounceTimer);

    if (value === '') {
      usernameStatus = '';
      usernameHint = '';
      return;
    }
    if (!USERNAME_RE.test(value)) {
      usernameStatus = 'format';
      usernameHint = 'Must be 2–30 chars (letters, numbers, _)';
      return;
    }

    usernameStatus = 'checking';
    usernameHint = 'Checking…';

    const mySeq = ++usernameCheckSeq;
    usernameDebounceTimer = setTimeout(async () => {
      try {
        const data = await api.get<{ available: boolean }>(
          `/auth/check-username?username=${encodeURIComponent(value)}`,
        );
        if (mySeq !== usernameCheckSeq) return;
        if (data.available) {
          usernameStatus = 'available';
          usernameHint = '✓ Available';
        } else {
          usernameStatus = 'taken';
          usernameHint = 'Username already taken';
        }
      } catch {
        if (mySeq !== usernameCheckSeq) return;
        usernameStatus = 'error';
        usernameHint = '';
      }
    }, 350);
  });

  /**
   * Shared validate-then-show-options helper used by both the deep-link
   * (#/register?code=xyz) and the manual gate-form submit. Stores the inviter
   * username so the options phase can greet the invitee.
   */
  async function validateAndEnter(code: string): Promise<boolean> {
    gateError = '';
    gateLoading = true;
    try {
      const data = await api.get<{ valid: boolean; inviter_username?: string }>(
        `/auth/validate-invite?code=${encodeURIComponent(code)}`,
      );
      if (data.valid) {
        inviterUsername = data.inviter_username ?? '';
        phase = 'options';
        return true;
      }
      gateError = 'This invite link is invalid or has expired.';
      return false;
    } catch {
      gateError = 'Something went wrong. Try again.';
      return false;
    } finally {
      gateLoading = false;
    }
  }

  async function handleValidateCode(e: Event) {
    e.preventDefault();
    if (!inviteCode.trim()) {
      gateError = 'Please enter an invite code';
      return;
    }
    await validateAndEnter(inviteCode.trim());
  }

  async function handleRegister(e: Event) {
    e.preventDefault();
    if (password.length < 8) {
      regError = 'Password must be at least 8 characters';
      return;
    }
    if (holdActive) {
      regError = NEUTRAL_REFUSAL;
      return;
    }
    const birth = composeBirthMonth(birthYear, birthMonth);
    if (!birth) {
      regError = 'Please enter your birth month and year';
      return;
    }
    if (!privacyAccepted) {
      regError = 'Please accept the Terms and Privacy Policy to continue.';
      return;
    }
    regError = '';
    regLoading = true;
    try {
      await api.post('/auth/signup', {
        username: username.trim(),
        email: email.trim(),
        password,
        invite_code: inviteCode.trim() || undefined,
        birth_month: birth,
        privacy_accepted: true,
      });
      await login(username.trim(), password);
      trackEvent('signup', {});
      navigate('/onboarding');
    } catch (err) {
      const msg = (err as Error).message || 'Registration failed';
      if (isAgeRefusal(msg)) {
        startSignupHold();
        holdActive = true;
        regError = NEUTRAL_REFUSAL;
      } else {
        regError = msg;
      }
    } finally {
      regLoading = false;
    }
  }

  async function handleWaitlist(e: Event) {
    e.preventDefault();
    if (!waitlistEmail.trim()) return;
    waitlistLoading = true;
    waitlistMsg = '';
    try {
      const data = await api.post<{ message: string }>('/auth/waitlist', { email: waitlistEmail.trim() });
      waitlistMsg = data.message || "Thanks! We'll be in touch.";
    } catch {
      waitlistMsg = 'Something went wrong. Try again.';
    } finally {
      waitlistLoading = false;
    }
  }
</script>

{#if phase === 'gate'}
  <div class="register-page">
    <form class="auth-card" onsubmit={handleValidateCode}>
      <h2>Join Beat Duel</h2>
      <p class="subtitle">Have an invite code?</p>
      {#if gateError}<p class="msg--error">{gateError}</p>{/if}
      {#if holdActive}<p class="msg--error">{NEUTRAL_REFUSAL}</p>{/if}
      <label class="field"
        ><span class="field-label">Invite Code</span>
        <input
          class="input"
          type="text"
          bind:value={inviteCode}
          bind:this={codeInput}
          required
          autocomplete="off"
        /></label
      >
      <button class="btn btn-primary full-width" type="submit" disabled={gateLoading}>
        {gateLoading ? 'Checking...' : 'Continue'}
      </button>
    </form>

    <div class="divider"><span>or</span></div>

    <div class="auth-card waitlist-card">
      <h3>Not ready yet? Join the waitlist</h3>
      <form class="waitlist-form" onsubmit={handleWaitlist}>
        <label for="waitlist-email" class="sr-only">Email address</label>
        <input
          id="waitlist-email"
          class="input"
          type="email"
          placeholder="your@email.com"
          bind:value={waitlistEmail}
          required
        />
        <button class="btn btn-primary" type="submit" disabled={waitlistLoading}>
          {waitlistLoading ? 'Joining...' : 'Join Waitlist'}
        </button>
      </form>
      {#if waitlistMsg}<p class="waitlist-msg">{waitlistMsg}</p>{/if}
    </div>

    <p class="auth-link">Already have an account? <a href="#/login">Sign In</a></p>
  </div>
{:else if phase === 'options'}
  <div class="register-page">
    <div class="auth-card">
      <h2>Join Beat Duel</h2>
      {#if inviterUsername}
        <p class="subtitle">You've been invited by <strong>@{inviterUsername}</strong></p>
      {:else}
        <p class="subtitle">You've been invited! Pick how you'd like to sign up.</p>
      {/if}
      {#if optionsError}<p class="msg--error">{optionsError}</p>{/if}
      {#if holdActive}<p class="msg--error">{NEUTRAL_REFUSAL}</p>{/if}

      <button
        class="btn btn-social btn-social--apple full-width"
        type="button"
        onclick={handleAppleSignup}
        disabled={holdActive || googleLoading || tiktokLoading || spotifyLoading || appleLoading}
      >
        <svg class="social-icon" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
          <path
            d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516.024.034 1.52.087 2.475-1.258.955-1.345.762-2.391.728-2.43zm3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422.212-2.189 1.675-2.789 1.698-2.854.023-.065-.597-.79-1.254-1.157a3.692 3.692 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56.244.729.625 1.924 1.273 2.796.576.984 1.34 1.667 1.659 1.899.319.232 1.219.386 1.843.067.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758.347-.79.505-1.217.473-1.282z"
          />
        </svg>
        {appleLoading ? 'Redirecting...' : 'Continue with Apple'}
      </button>

      <button
        class="btn btn-social btn-social--google full-width"
        type="button"
        onclick={handleGoogleSignup}
        disabled={holdActive || googleLoading || tiktokLoading || spotifyLoading || appleLoading}
      >
        <svg class="social-icon" viewBox="0 0 18 18" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
          />
          <path
            fill="#34A853"
            d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
          />
          <path
            fill="#FBBC05"
            d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
          />
          <path
            fill="#EA4335"
            d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
          />
        </svg>
        {googleLoading ? 'Redirecting...' : 'Continue with Google'}
      </button>

      <button
        class="btn btn-social btn-social--tiktok full-width"
        type="button"
        onclick={handleTikTokSignup}
        disabled={holdActive || googleLoading || tiktokLoading || spotifyLoading || appleLoading}
      >
        {tiktokLoading ? 'Redirecting...' : 'Continue with TikTok'}
      </button>

      <button
        class="btn btn-social btn-social--spotify full-width"
        type="button"
        onclick={handleSpotifySignup}
        disabled={holdActive || googleLoading || tiktokLoading || spotifyLoading || appleLoading}
      >
        {spotifyLoading ? 'Redirecting...' : 'Continue with Spotify'}
      </button>

      <button
        class="btn btn-primary full-width"
        type="button"
        onclick={() => (phase = 'form')}
        disabled={holdActive || googleLoading || tiktokLoading || spotifyLoading || appleLoading}
      >
        Continue with Email
      </button>
    </div>

    <p class="auth-link">Already have an account? <a href="#/login">Sign In</a></p>
  </div>
{:else}
  <div class="register-page">
    <form class="auth-card" onsubmit={handleRegister}>
      <h2>Create Account</h2>
      {#if regError}<p class="msg--error">{regError}</p>{/if}
      <label class="field"
        ><span class="field-label">Username</span>
        <input
          class="input"
          type="text"
          bind:value={username}
          bind:this={usernameInput}
          required
          maxlength="30"
          autocomplete="username"
          aria-describedby="username-hint"
          aria-invalid={usernameStatus === 'format' || usernameStatus === 'taken'}
        />
        {#if usernameHint}
          <p
            id="username-hint"
            class="field-hint field-hint--{usernameStatus}"
            data-testid="username-status"
            aria-live="polite"
          >
            {usernameHint}
          </p>
        {/if}</label
      >
      <label class="field"
        ><span class="field-label">Email</span>
        <input class="input" type="email" bind:value={email} required autocomplete="email" /></label
      >
      <PasswordInput
        id="password"
        label="Password"
        bind:value={password}
        required
        minlength={8}
        autocomplete="new-password"
        showMeter
      />
      <fieldset class="field dob-field">
        <legend class="field-label">Birth month</legend>
        <div class="dob-row">
          <select class="input dob-select" bind:value={birthMonth} required aria-label="Month" disabled={holdActive}>
            <option value="" disabled>Month</option>
            {#each BIRTH_MONTHS as m (m)}
              <option value={m}>{m}</option>
            {/each}
          </select>
          <select class="input dob-select" bind:value={birthYear} required aria-label="Year" disabled={holdActive}>
            <option value="" disabled>Year</option>
            {#each years as y (y)}
              <option value={y}>{y}</option>
            {/each}
          </select>
        </div>
      </fieldset>
      <label class="field check-field">
        <input type="checkbox" bind:checked={privacyAccepted} required />
        <span>
          I agree to the
          <a href="#/terms" target="_blank" rel="noopener">Terms</a> and
          <a href="#/privacy" target="_blank" rel="noopener">Privacy Policy</a>.
        </span>
      </label>
      <button class="btn btn-primary full-width" type="submit" disabled={regLoading || holdActive}>
        {regLoading ? 'Creating account...' : 'Sign Up'}
      </button>
    </form>

    <button class="back-link" onclick={() => (phase = inviterUsername || codeParam ? 'options' : 'gate')}
      >&larr; Back</button
    >
    <p class="auth-link">Already have an account? <a href="#/login">Sign In</a></p>
  </div>
{/if}

<style>
  .register-page {
    max-width: 400px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    animation: fadeIn 0.25s ease-out;
  }
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .auth-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }
  .auth-card h2 {
    text-align: center;
    color: var(--text);
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 0.25rem;
    -webkit-text-fill-color: var(--text);
    background: none;
    background-clip: unset;
    -webkit-background-clip: unset;
  }
  .subtitle {
    text-align: center;
    color: var(--text-muted);
    font-size: 0.85rem;
    margin: 0;
  }
  .field {
    display: block;
  }
  .field-label {
    display: block;
    font-size: 0.875rem;
    color: var(--text-muted);
    margin-bottom: 0.25rem;
  }
  .full-width {
    width: 100%;
    padding: 0.625rem;
  }
  .input {
    max-width: 100%;
  }

  .field-hint {
    margin: 0.25rem 0 0;
    font-size: 0.75rem;
    line-height: 1.2;
    color: var(--text-muted);
  }
  .field-hint--format,
  .field-hint--taken {
    color: var(--pink);
  }
  .field-hint--available {
    color: var(--green);
  }
  .field-hint--checking {
    color: var(--text-muted);
  }

  .divider {
    text-align: center;
    position: relative;
    margin: 0.25rem 0;
  }
  .divider span {
    background: var(--bg);
    padding: 0 0.75rem;
    color: var(--text-muted);
    font-size: 0.8rem;
    position: relative;
    z-index: 1;
  }
  .divider::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--border);
  }

  .waitlist-card {
    text-align: center;
  }
  .waitlist-card h3 {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--text-secondary);
    margin: 0 0 0.75rem;
  }
  .waitlist-form {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
  }
  .waitlist-form .input {
    max-width: 220px;
  }
  .waitlist-msg {
    margin-top: 0.5rem;
    font-size: 0.85rem;
    color: var(--cyan);
  }

  .auth-link {
    text-align: center;
    font-size: 0.85rem;
    color: var(--text-muted);
  }
  .auth-link a {
    color: var(--cyan);
    text-decoration: underline;
  }
  .auth-link a:hover {
    text-decoration: none;
  }

  .back-link {
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 0.85rem;
    text-align: center;
    padding: 0;
  }
  .back-link:hover {
    color: var(--text);
  }

  .dob-field {
    border: none;
    padding: 0;
    margin: 0;
  }
  .dob-row {
    display: flex;
    gap: 0.5rem;
  }
  .dob-select {
    flex: 1;
    min-width: 0;
  }
  .hint {
    margin: 0.35rem 0 0;
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .check-field {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.8rem;
    color: var(--text-muted);
    line-height: 1.4;
  }
  .check-field input[type='checkbox'] {
    margin-top: 0.15rem;
    flex-shrink: 0;
  }
  .check-field a {
    color: var(--cyan);
    text-decoration: underline;
  }
  .check-field a:hover {
    text-decoration: none;
  }

  .btn-social {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
    background: transparent;
    color: var(--text);
  }
  .btn-social:hover {
    opacity: 0.85;
  }
  .btn-social--google {
    border: 1px solid #4285f4;
  }
  .btn-social--tiktok {
    border: 1px solid #69c9d0;
  }
  .btn-social--spotify {
    background: #1db954;
    color: #fff;
    border: none;
  }
  .btn-social--spotify:hover:not(:disabled) {
    background: #1aa34a;
  }
  .btn-social--apple {
    background: #000;
    color: #fff;
    border: none;
  }
  .btn-social--apple:hover:not(:disabled) {
    background: #1a1a1a;
  }
  .social-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  @media (max-width: 640px) {
    .waitlist-form {
      flex-direction: column;
      align-items: center;
    }
  }
</style>
